import { Stack, Typography } from '@mui/material';
import Widget from '@/components/common/widget.tsx';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import type { PlayerDto } from '@/types/dtos/player/response/player.dto';
import type { PlayerParentEntity } from '@/types/dtos/player/player-parent-entity';
import type { LiveContextDto } from '@/types/dtos/live-contexts/live-context.dto';
import { ConfigurationPlayerSection } from './configuration-player-section';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { REMOTE_SPEAKERSHIP_FEATURE, SPEAKER_CONFIDENCE_MONITORS_FEATURE } from '@/constants/feature-flags';
import { ConfidenceSettingsPanel } from './confidence-settings-panel';

type StageConfigurationWidgetProps = {
  stagePlayers: PlayerDto[];
  confidencePlayers?: PlayerDto[];
  /** Audience cam (one camera facing the audience, shown only to remote speakers). */
  houseCamPlayers?: PlayerDto[];
  entityId: string;
  entityType: PlayerParentEntity;
  eventSlotId?: string | null;
  liveContext?: LiveContextDto | null;
  disabled?: boolean;
  videoPlayerId?: string | null;
  audioPlayerId?: string | null;
  /** Speaker monitor that sends the audience picture when no audience cam is connected. */
  audienceMonitorPlayerId?: string | null;
  /** Connected audience cam that has taken over the audience picture. */
  audienceCamPlayerId?: string | null;
};

export function StageConfigurationWidget({
  stagePlayers,
  confidencePlayers = [],
  houseCamPlayers = [],
  entityId,
  entityType,
  eventSlotId,
  liveContext,
  disabled,
  videoPlayerId,
  audioPlayerId,
  audienceMonitorPlayerId,
  audienceCamPlayerId,
}: StageConfigurationWidgetProps) {
  const t = useTranslations();
  const { hasAccess: hasSpeakerConfidenceMonitors, isLoading: speakerConfidenceLoading } = useFeatureAccess(
    SPEAKER_CONFIDENCE_MONITORS_FEATURE,
  );
  const showSpeakerConfidenceMonitors = !speakerConfidenceLoading && hasSpeakerConfidenceMonitors;
  const { hasAccess: hasRemoteSpeakership, isLoading: remoteSpeakershipLoading } =
    useFeatureAccess(REMOTE_SPEAKERSHIP_FEATURE);
  const showHouseCam = entityType === 'Flow' && !remoteSpeakershipLoading && hasRemoteSpeakership;

  return (
    <Widget title={t('flows.detail.configuration.stage.title' as TranslationKey)} titleVariant="h6">
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('flows.detail.configuration.stage.intro' as TranslationKey)}
        </Typography>

        <ConfigurationPlayerSection
          title={t('flows.detail.configuration.stage.presentationDevice.title' as TranslationKey)}
          intro={t('flows.detail.configuration.stage.presentationDevice.intro' as TranslationKey)}
          players={stagePlayers}
          playerType="stagePlayer"
          alwaysShowPairFormWhenEmpty
          entityId={entityId}
          entityType={entityType}
          eventSlotId={eventSlotId}
          liveContext={liveContext}
          disabled={disabled}
          videoPlayerId={videoPlayerId}
          audioPlayerId={audioPlayerId}
        />

        {showSpeakerConfidenceMonitors && (
          <>
            <ConfigurationPlayerSection
              title={t('flows.detail.configuration.stage.speakerMonitor.title' as TranslationKey)}
              intro={t('flows.detail.configuration.stage.speakerMonitor.intro' as TranslationKey)}
              players={confidencePlayers}
              playerType="confidencePlayer"
              entityId={entityId}
              entityType={entityType}
              eventSlotId={eventSlotId}
              liveContext={liveContext}
              disabled={disabled}
              videoPlayerId={videoPlayerId}
              audioPlayerId={audioPlayerId}
              audienceCameraPlayerId={audienceMonitorPlayerId}
            />
            {entityType === 'Flow' ? <ConfidenceSettingsPanel entity={{ entityType, entityId }} /> : null}
          </>
        )}

        {showHouseCam && (
          <ConfigurationPlayerSection
            title={t('flows.detail.configuration.stage.houseCam.title' as TranslationKey)}
            intro={t('flows.detail.configuration.stage.houseCam.intro' as TranslationKey)}
            players={houseCamPlayers}
            playerType="houseCamPlayer"
            entityId={entityId}
            entityType={entityType}
            eventSlotId={eventSlotId}
            liveContext={liveContext}
            disabled={disabled}
            videoPlayerId={videoPlayerId}
            audioPlayerId={audioPlayerId}
            audienceCameraPlayerId={audienceCamPlayerId}
          />
        )}
      </Stack>
    </Widget>
  );
}
